import { useEffect, useMemo, useRef, useState } from "react";
import api, { API } from "@/lib/api";
import { formatRupeesCompact } from "@/lib/format";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  FileText, Sheet as SheetIcon, Printer, Play, Search,
  ChevronDown, ChevronUp, Loader2, Briefcase,
} from "lucide-react";
import {
  CATALOG, REPORT_BY_KEY, PRESETS, presetRange,
  isNumeric, looksLikeMoney, isTotalRow,
} from "./reports/catalog";
import ReportCatalogSheet from "./reports/ReportCatalogSheet";
import ColumnsPopover from "./reports/ColumnsPopover";
import ReportTable from "./reports/ReportTable";
import FYPicker, { fyToRange } from "@/components/FYPicker";

export default function Reports() {
  const [activeKey, setActiveKey] = useState("investment_summary");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [presetActive, setPresetActive] = useState("all");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [sheets, setSheets] = useState(null);
  const [activeSheet, setActiveSheet] = useState(null);
  const [sort, setSort] = useState(null);
  const [catalogOpen, setCatalogOpen] = useState(false);
  const [showAnalysis, setShowAnalysis] = useState(true);
  const [hiddenCols, setHiddenCols] = useState({});
  const [freezeFirst, setFreezeFirst] = useState(true);
  const [fyValue, setFyValue] = useState("all");
  const [investmentId, setInvestmentId] = useState("");
  const [investments, setInvestments] = useState([]);
  const printRef = useRef(null);
  const active = REPORT_BY_KEY[activeKey];
  const scopedInv = investments.find((i) => i.id === investmentId);

  useEffect(() => {
    api.get("/investments").then((r) => setInvestments(r.data || [])).catch(() => {});
  }, []);

  const applyPreset = (kind) => {
    setPresetActive(kind);
    const [f, t] = presetRange(kind);
    setFrom(f); setTo(t);
  };

  const run = async () => {
    setLoading(true);
    setSheets(null);
    setSort(null);
    setSearch("");
    try {
      const q = new URLSearchParams();
      if (from) q.set("from_date", from);
      if (to) q.set("to_date", to);
      if (investmentId) q.set("investment_id", investmentId);
      const r = await api.get(`/reports/${activeKey}.json?${q.toString()}`);
      setSheets(r.data);
      setActiveSheet(Object.keys(r.data)[0]);
      setHiddenCols({});
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to generate report");
    } finally { setLoading(false); }
  };

  useEffect(() => { run(); /* eslint-disable-next-line */ }, [activeKey, investmentId]);

  const pickReport = (key) => { setActiveKey(key); setCatalogOpen(false); };

  const dl = async (fmt) => {
    const token = localStorage.getItem("mv_token");
    const q = new URLSearchParams();
    if (from) q.set("from_date", from);
    if (to) q.set("to_date", to);
    if (investmentId) q.set("investment_id", investmentId);
    const url = `${API}/reports/${activeKey}.${fmt}?${q.toString()}`;
    try {
      const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error("Failed");
      const blob = await res.blob();
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = `MARKSAM_${activeKey}_${from || "all"}_${to || "now"}.${fmt}`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success(`Downloaded ${fmt.toUpperCase()}`);
    } catch { toast.error("Download failed"); }
  };

  const doPrint = () => {
    if (!printRef.current) return;
    const w = window.open("", "_blank", "width=1024,height=768");
    const styles = `
      <style>
        body{font-family:'Inter',system-ui,-apple-system,sans-serif;padding:24px;color:#0f172a}
        h1{font-size:22px;margin:0 0 4px}
        .meta{color:#64748b;font-size:12px;margin-bottom:16px;border-bottom:1px solid #e2e8f0;padding-bottom:12px}
        table{width:100%;border-collapse:collapse;font-size:12px}
        th{background:#f8fafc;text-align:left;padding:8px;border-bottom:1px solid #cbd5e1;font-weight:600}
        td{padding:6px 8px;border-bottom:1px solid #f1f5f9}
        .num{text-align:right;font-variant-numeric:tabular-nums;font-family:'IBM Plex Mono',monospace}
        .total td{font-weight:700;background:#fef2f2;border-top:2px solid #dc2626}
      </style>`;
    w.document.write(`<html><head><title>${active.title}</title>${styles}</head><body>`);
    w.document.write(`<h1>${active.title}${scopedInv ? ` — ${scopedInv.name}` : ""}</h1>`);
    w.document.write(`<div class="meta">MARKSAM Ventures LLP · ${from || "All-time"} → ${to || "Today"}${scopedInv ? ` · Project: ${scopedInv.name}` : ""} · ${new Date().toLocaleString()}</div>`);
    w.document.write(printRef.current.innerHTML);
    w.document.write("</body></html>");
    w.document.close();
    setTimeout(() => { w.focus(); w.print(); }, 250);
  };

  const sheetNames = sheets ? Object.keys(sheets) : [];
  const rows = sheets && activeSheet ? sheets[activeSheet] : [];
  const allColumns = rows.length ? Object.keys(rows[0]) : [];
  const columns = allColumns.filter((c) => !hiddenCols[c]);

  const filteredRows = useMemo(() => {
    const s = search.trim().toLowerCase();
    let out = rows;
    if (s) out = out.filter((r) => Object.values(r).some((v) => String(v ?? "").toLowerCase().includes(s)));
    if (sort) {
      const { col, dir } = sort;
      out = [...out].sort((a, b) => {
        if (isTotalRow(a)) return 1;
        if (isTotalRow(b)) return -1;
        const av = a[col], bv = b[col];
        if (isNumeric(av) && isNumeric(bv)) return dir === "asc" ? av - bv : bv - av;
        return dir === "asc"
          ? String(av ?? "").localeCompare(String(bv ?? ""))
          : String(bv ?? "").localeCompare(String(av ?? ""));
      });
    }
    return out;
  }, [rows, search, sort]);

  const analysis = useMemo(() => {
    const stats = {};
    allColumns.forEach((c) => {
      const nums = rows.filter((r) => !isTotalRow(r)).map((r) => r[c]).filter(isNumeric);
      if (nums.length) {
        stats[c] = {
          sum: nums.reduce((a, b) => a + b, 0),
          avg: nums.reduce((a, b) => a + b, 0) / nums.length,
          count: nums.length,
          isMoney: looksLikeMoney(c),
        };
      }
    });
    return stats;
  }, [rows, allColumns]);

  const numericCols = Object.keys(analysis);

  const toggleSort = (col) => {
    setSort((prev) => {
      if (!prev || prev.col !== col) return { col, dir: "desc" };
      if (prev.dir === "desc") return { col, dir: "asc" };
      return null;
    });
  };

  const toggleCol = (c) => setHiddenCols((h) => ({ ...h, [c]: !h[c] }));
  const showAllCols = () => setHiddenCols({});
  const hideMostCols = () => {
    const h = {};
    allColumns.slice(5).forEach((c) => { h[c] = true; });
    setHiddenCols(h);
  };

  return (
    <div className="space-y-5">
      {/* Header — compact */}
      <div>
        <div className="eyebrow">Analytics · Insights</div>
        <h1 className="mt-2 font-editorial font-light text-[34px] leading-[1.02] sm:text-4xl md:text-5xl text-slate-900 tracking-[-0.035em]" data-testid="reports-title">
          <em className="italic">Insights.</em>
        </h1>
      </div>

      {/* Ribbon */}
      <div className="border border-slate-200 bg-white rounded-md">
        <div className="flex items-center gap-2 p-3 flex-wrap">
          <ReportCatalogSheet
            active={active}
            activeKey={activeKey}
            open={catalogOpen}
            onOpenChange={setCatalogOpen}
            onPick={pickReport}
          />

          <div className="hidden md:block h-8 border-l border-slate-200 mx-1" />

          <FYPicker
            value={fyValue}
            onChange={(v) => {
              setFyValue(v);
              const r = fyToRange(v);
              setFrom(r.from); setTo(r.to);
              setPresetActive("");
            }}
            size="sm"
          />

          <Select value={investmentId || "all"} onValueChange={(v) => setInvestmentId(v === "all" ? "" : v)}>
            <SelectTrigger className="h-8 w-[190px] text-[12px] gap-1.5" data-testid="rep-project">
              <Briefcase className="h-3.5 w-3.5 text-slate-400 shrink-0" />
              <SelectValue placeholder="All investments" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All investments</SelectItem>
              {investments.map((i) => <SelectItem key={i.id} value={i.id}>{i.name}</SelectItem>)}
            </SelectContent>
          </Select>

          <div className="flex items-center gap-1.5">
            <Input type="date" value={from} onChange={(e) => { setFrom(e.target.value); setPresetActive(""); setFyValue("all"); }} className="h-8 w-[135px] text-[12px]" data-testid="rep-from" />
            <span className="text-slate-300 text-xs">→</span>
            <Input type="date" value={to} onChange={(e) => { setTo(e.target.value); setPresetActive(""); setFyValue("all"); }} className="h-8 w-[135px] text-[12px]" data-testid="rep-to" />
          </div>

          <div className="flex-1" />

          <Button
            onClick={run}
            disabled={loading}
            className="rounded-md bg-red-600 hover:bg-red-700 text-white gap-1.5 h-8 px-3 text-[12px]"
            data-testid="rep-generate"
          >
            {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Play className="h-3.5 w-3.5" />}
            Generate
          </Button>

          {allColumns.length > 0 && (
            <ColumnsPopover
              allColumns={allColumns}
              hiddenCols={hiddenCols}
              onToggleCol={toggleCol}
              onShowAll={showAllCols}
              onCompact={hideMostCols}
              freezeFirst={freezeFirst}
              onFreezeChange={setFreezeFirst}
              numericColumns={numericCols}
            />
          )}

          <Button onClick={() => dl("pdf")} variant="outline" className="h-8 gap-1.5 rounded-md text-[12px]" data-testid="rep-dl-pdf">
            <FileText className="h-3.5 w-3.5" /> PDF
          </Button>
          <Button onClick={() => dl("xlsx")} variant="outline" className="h-8 gap-1.5 rounded-md text-[12px]" data-testid="rep-dl-xlsx">
            <SheetIcon className="h-3.5 w-3.5" /> Excel
          </Button>
          <Button onClick={doPrint} variant="outline" className="h-8 gap-1.5 rounded-md text-[12px]" data-testid="rep-print">
            <Printer className="h-3.5 w-3.5" /> Print
          </Button>
        </div>

        {/* Row 2: preset chips + search */}
        <div className="flex items-center gap-2 px-3 pb-3 flex-wrap border-t border-slate-100 pt-2">
          <div className="text-[10px] tracking-wider uppercase text-slate-400 font-medium mr-1">Quick</div>
          {PRESETS.map(([k, label]) => (
            <button
              key={k}
              onClick={() => applyPreset(k)}
              data-testid={`preset-${k}`}
              className={`text-[11px] font-medium px-2.5 py-1 rounded-full border transition-colors ${
                presetActive === k
                  ? "bg-slate-900 text-white border-slate-900"
                  : "bg-white text-slate-600 border-slate-200 hover:border-slate-400"
              }`}
            >
              {label}
            </button>
          ))}
          <div className="flex-1 min-w-[120px]" />
          {sheets && rows.length > 0 && (
            <div className="relative w-full sm:w-[260px]">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={`Search ${rows.length} rows…`}
                className="pl-8 h-8 text-[12px]"
                data-testid="rep-search"
              />
            </div>
          )}
        </div>
      </div>

      {/* Sheet tabs + analysis toggle */}
      {!loading && sheets && (
        <div className="flex items-center gap-3 flex-wrap">
          {scopedInv && (
            <span className="inline-flex items-center gap-1.5 text-[11px] font-medium px-2.5 py-1.5 rounded-md border border-red-200 bg-red-50 text-red-700" data-testid="rep-scope-chip">
              <Briefcase className="h-3 w-3" /> Scoped to: {scopedInv.name}
              <button onClick={() => setInvestmentId("")} className="ml-0.5 text-red-400 hover:text-red-700" aria-label="Clear investment filter">×</button>
            </span>
          )}
          {sheetNames.length > 1 ? (
            <div className="flex items-center gap-1 flex-wrap">
              {sheetNames.map((n) => (
                <button
                  key={n}
                  onClick={() => { setActiveSheet(n); setSort(null); setHiddenCols({}); }}
                  data-testid={`sheet-${n}`}
                  className={`text-[11px] font-medium px-2.5 py-1.5 rounded-md border transition-colors ${
                    activeSheet === n
                      ? "bg-slate-900 text-white border-slate-900"
                      : "bg-white text-slate-600 border-slate-200 hover:border-slate-400"
                  }`}
                >
                  {n} <span className={`ml-1 text-[9px] font-mono-fin ${activeSheet === n ? "text-white/70" : "text-slate-400"}`}>{sheets[n].length}</span>
                </button>
              ))}
            </div>
          ) : (
            <div className="text-[11px] text-slate-500 font-medium">
              {active.title} <span className="text-slate-400">·</span> {rows.length} rows
              {search && <> <span className="text-slate-400">·</span> {filteredRows.length} match</>}
            </div>
          )}
          <div className="flex-1" />
          {numericCols.length > 0 && (
            <button
              onClick={() => setShowAnalysis((v) => !v)}
              className="text-[11px] text-slate-500 hover:text-slate-900 inline-flex items-center gap-1"
              data-testid="rep-toggle-analysis"
            >
              {showAnalysis ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
              {showAnalysis ? "Hide" : "Show"} analysis
            </button>
          )}
        </div>
      )}

      {/* Analysis strip */}
      {!loading && sheets && numericCols.length > 0 && showAnalysis && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="rep-analysis">
          {numericCols.slice(0, 4).map((c) => {
            const s = analysis[c];
            return (
              <div key={c} className="border border-slate-200 bg-white rounded-md p-3">
                <div className="text-[9px] tracking-wider uppercase text-slate-400 truncate">{c}</div>
                <div className="mt-0.5 font-mono-fin text-[15px] text-slate-900">
                  {s.isMoney ? formatRupeesCompact(s.sum) : s.sum.toLocaleString("en-IN")}
                </div>
                <div className="text-[9px] text-slate-400">
                  avg {s.isMoney ? formatRupeesCompact(s.avg) : s.avg.toFixed(1)} · n={s.count}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Loading / Empty */}
      {loading && (
        <div className="border border-slate-200 bg-white rounded-md p-16 flex flex-col items-center justify-center gap-3 text-slate-400">
          <Loader2 className="h-6 w-6 animate-spin" />
          <div className="text-[13px]">Generating…</div>
        </div>
      )}
      {!loading && sheets && rows.length === 0 && (
        <div className="border border-slate-200 bg-white rounded-md p-16 flex flex-col items-center justify-center gap-2 text-slate-500">
          <div className="text-[13px] font-medium">No data for this range</div>
          <div className="text-[11px] text-slate-400">Try widening the date range or picking a different preset.</div>
        </div>
      )}

      {/* Table */}
      {!loading && sheets && rows.length > 0 && (
        <ReportTable
          ref={printRef}
          rows={filteredRows}
          columns={columns}
          analysis={analysis}
          sort={sort}
          onToggleSort={toggleSort}
          freezeFirst={freezeFirst}
          search={search}
          footerLeft={`MARKSAM Ventures LLP · ${from || "All-time"} → ${to || "Today"}`}
          footerRight={
            <>
              {columns.length !== allColumns.length && (
                <span className="text-red-600 mr-3">{allColumns.length - columns.length} col hidden</span>
              )}
              Generated {new Date().toLocaleString("en-IN")}
            </>
          }
        />
      )}
    </div>
  );
}
